// Phase 189 (M-116): AddCategoryModal — lightweight bottom-sheet drawer
// for creating a new category from inside AddTransactionModal. Triggered
// by the "+ 新增類別" button below the category pager.
//
// Reuses the Phase 98 drag-down-to-dismiss pattern from
// AddTransactionSheet.jsx (kept the same touch handlers). Form fields:
//
//   - Name (text, required, max 20 chars)
//   - Icon (12 quick-pick emoji buttons)
//   - Color (5 brand color swatches)
//
// The `type` prop locks the new category to either 'expense' or
// 'income' (matches whatever the AddTransactionModal's type toggle is
// currently on) — no expense/income toggle in this drawer.
//
// Save flow:
//   1. Validate name (non-empty after trim)
//   2. writes.createCategory({name, icon, color, type, sort_order: 100})
//   3. onCreated(row) — AddTransactionModal auto-selects the new
//      category via setCategoryId(row.local_uuid) and refreshes
//      categories4Type from data.getCategories().
//
// On error: in-sheet error banner, save button re-enabled for retry.
//
// Usage:
//   <AddCategoryModal
//     open={addCategoryOpen}
//     type={type}            // 'expense' | 'income'
//     onClose={() => setAddCategoryOpen(false)}
//     onCreated={(cat) => {
//       setCategoryId(cat.local_uuid);
//       setAddCategoryOpen(false);
//     }}
//   />
import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n/index.jsx';
import { writes } from '../lib/dataService.js';

// Quick-pick set — 12 emoji to cover the common needs without making
// the drawer feel like a picker. Power users can change the icon in
// Settings → Categories later. Selected subset of Settings.jsx
// ICON_PRESETS.
const ICON_OPTIONS = [
  '🍔', '🚇', '🛍️', '🎬', '🏠', '💊',
  '📚', '💰', '🎁', '☕', '🚗', '✨',
];

// 5 brand colors — picked from COLOR_PRESETS for harmony with the
// existing category swatches across the app.
const COLOR_OPTIONS = [
  '#ef4444', '#f59e0b', '#3b82f6', '#10b981', '#8b5cf6',
];

export default function AddCategoryModal({
  open,
  type,           // 'expense' | 'income'
  onClose,
  onCreated,
}) {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [icon, setIcon] = useState(ICON_OPTIONS[0]);
  const [color, setColor] = useState(COLOR_OPTIONS[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const dialogRef = useRef(null);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(0);
  const dragging = useRef(false);
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;

  // Reset form when modal opens — so reopening doesn't show the
  // previous draft.
  useEffect(() => {
    if (open) {
      setName('');
      setIcon(ICON_OPTIONS[0]);
      setColor(COLOR_OPTIONS[0]);
      setError(null);
      setSaving(false);
    }
  }, [open]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Escape closes (desktop).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, saving]);

  // Reset drag state on close.
  useEffect(() => {
    if (!open) setDragY(0);
  }, [open]);

  if (!open) return null;

  // Phase 98 drag-down-to-dismiss. Only enabled on mobile.
  const onHandleTouchStart = (e) => {
    if (!isMobile) return;
    dragStartY.current = e.touches[0].clientY;
    dragging.current = true;
  };
  const onHandleTouchMove = (e) => {
    if (!dragging.current) return;
    const dy = e.touches[0].clientY - dragStartY.current;
    if (dy > 0) setDragY(dy);
  };
  const onHandleTouchEnd = () => {
    if (!dragging.current) return;
    dragging.current = false;
    if (dragY > window.innerHeight * 0.3) {
      if (!saving) onClose();
    }
    setDragY(0);
  };

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      // sort_order 100 matches Settings.jsx createCategory default so
      // new categories appear at the bottom of the picker.
      const row = await writes.createCategory({
        name: trimmed,
        icon,
        color,
        type,
        sort_order: 100,
      });
      onCreated(row);
    } catch (e) {
      setError(e?.message || String(e));
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50"
      onClick={() => { if (!saving) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={t('addCategory.title')}
      data-testid="add-category-modal"
    >
      <div
        ref={dialogRef}
        className="bg-white dark:bg-slate-800 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col"
        style={{
          maxHeight: 'min(80vh, 80dvh)',
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          transition: dragging.current ? 'none' : 'transform 200ms ease-out',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle — mobile only. */}
        <div
          className="sm:hidden pt-2 pb-1 flex justify-center cursor-grab active:cursor-grabbing"
          onTouchStart={onHandleTouchStart}
          onTouchMove={onHandleTouchMove}
          onTouchEnd={onHandleTouchEnd}
          aria-hidden="true"
        >
          <div className="w-12 h-1 bg-slate-300 dark:bg-slate-600 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            {t('addCategory.title')}
          </h2>
          <button
            onClick={() => { if (!saving) onClose(); }}
            aria-label={t('common.cancel')}
            data-testid="add-category-cancel"
            className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 px-2 py-1 min-w-[44px] min-h-[44px]"
          >
            {t('common.cancel')}
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {/* Name input */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
              {t('addCategory.nameLabel')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 20))}
              placeholder={t('addCategory.namePlaceholder')}
              maxLength={20}
              data-testid="add-category-name"
              className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-slate-100 rounded-lg text-sm focus:border-brand-500 outline-none min-h-[44px]"
            />
            <div className="text-[10px] text-slate-400 mt-1 text-right">
              {trimmed.length}/20
            </div>
          </div>

          {/* Icon picker — 6×2 grid */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
              {t('addCategory.iconLabel')}
            </label>
            <div className="grid grid-cols-6 gap-1.5">
              {ICON_OPTIONS.map((ic) => {
                const selected = icon === ic;
                return (
                  <button
                    key={ic}
                    type="button"
                    onClick={() => setIcon(ic)}
                    data-testid={`add-category-icon-${ic}`}
                    aria-pressed={selected}
                    aria-label={ic}
                    className={[
                      'aspect-square rounded-md text-xl flex items-center justify-center transition-colors min-h-[44px]',
                      selected
                        ? 'bg-brand-100 dark:bg-brand-900 ring-2 ring-brand-500'
                        : 'bg-slate-50 dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600',
                    ].join(' ')}
                  >
                    {ic}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color picker — 5 swatches */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
              {t('addCategory.colorLabel')}
            </label>
            <div className="flex gap-2">
              {COLOR_OPTIONS.map((co) => {
                const selected = color === co;
                return (
                  <button
                    key={co}
                    type="button"
                    onClick={() => setColor(co)}
                    data-testid={`add-category-color-${co}`}
                    data-color={co}
                    aria-pressed={selected}
                    aria-label={co}
                    className={[
                      'w-10 h-10 rounded-md transition-shadow min-h-[44px]',
                      selected ? 'ring-2 ring-offset-2 ring-slate-700 dark:ring-offset-slate-800' : '',
                    ].join(' ')}
                    style={{ backgroundColor: co }}
                  />
                );
              })}
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div
              data-testid="add-category-error"
              className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2"
              role="alert"
            >
              {error}
            </div>
          )}
        </div>

        {/* Footer — save button */}
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            data-testid="add-category-save"
            className={[
              'w-full text-sm font-medium py-2.5 rounded-lg min-h-[44px] transition-colors',
              canSave
                ? 'bg-brand-600 hover:bg-brand-700 text-white'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed',
            ].join(' ')}
          >
            {saving ? t('common.loadingDots') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
